import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useGroupSettingsStore from '@modules/digital-signage/components/group-settings/store/group-settings.store.ts';
import {
  GroupDevicesProps,
  GroupSettingsFormValues
} from '@modules/digital-signage/model/group-settings.model.ts';
import GroupActions from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-actions/group-actions.tsx';
import GroupDetailsFields from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-details-fields/group-details-fields.tsx';
import GroupDevicesList from '@modules/digital-signage/components/group-settings/components/group-devices/components/group-devices-list/group-devices-list.tsx';

const GroupDevices: React.FC<GroupDevicesProps> = ({
  isDefaultGroup,
  addedDevicesCount,
  onReloadGroup,
  onDeleteGroup
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext<GroupSettingsFormValues>();
  const { devicesList, groupDetails, isDevicesLoading } =
    useGroupSettingsStore();

  const { field: devicesField } = useController({ control, name: 'devices' });
  const selectedDevices = devicesField.value ?? [];
  const groupName = useWatch({ control, name: 'groupName' });
  const hasGroupName = !!groupName?.trim();
  const isListLoading = isDevicesLoading();

  const devicesById = useMemo(
    () =>
      new Map(
        [...(groupDetails?.devices ?? []), ...devicesList].map((device) => [
          device._id,
          device
        ])
      ),
    [devicesList, groupDetails]
  );

  return (
    <div className="flex flex-col min-h-0 rounded-[14px] border border-(--gray-9) bg-(--white)">
      <div className="flex items-center h-14 px-4 shrink-0">
        <Typography
          variant="subtitle4"
          className="text-(--dark-1)! max-lg-xl:text-[16px]!"
        >
          {t('digital_signage_group_title')}
        </Typography>
        {onReloadGroup && onDeleteGroup && (
          <div className="ml-auto flex">
            <GroupActions onReload={onReloadGroup} onDelete={onDeleteGroup} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 min-h-0 px-4 pt-1 pb-4">
        <GroupDetailsFields
          isDefaultGroup={isDefaultGroup}
          hasGroupName={hasGroupName}
          addedDevicesCount={addedDevicesCount}
        />

        <GroupDevicesList
          selectedDevices={selectedDevices}
          devicesById={devicesById}
          devicesList={devicesList}
          disabled={!hasGroupName}
          isLoading={isListLoading}
          isAddDisabled={!hasGroupName || isListLoading}
          onDevicesChange={devicesField.onChange}
        />
      </div>
    </div>
  );
};

export default GroupDevices;
